import { Modal, useToast } from '@afterpaytouch/core'
import { PostVerifyEmailResponseBody } from '@afterpaytouch/portal-api/consumer/account/change-mobile/types'
import { useTranslation } from 'next-i18next'
import React, { createContext, Dispatch, FunctionComponent, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useChangeMobileVerifyEmailMutation } from '../../state/account/changeMobile'
import { Confirm, Edit, Init } from './steps'

const I18N_NAMESPACE = ['common', 'account']

export const noop = (): void => {}

export const EditMobileNumberSteps = {
  Init: 'Init',
  Edit: 'Edit',
  Confirm: 'Confirm',
} as const

export type EditMobileNumberStep = (typeof EditMobileNumberSteps)[keyof typeof EditMobileNumberSteps]

export interface EditMobileNumberModalContextProps {
  step: EditMobileNumberStep
  setStep: Dispatch<SetStateAction<EditMobileNumberStep>>
  token: string
  setToken: Dispatch<SetStateAction<string>>
  newMobile: string
  setNewMobile: Dispatch<SetStateAction<string>>
  resetState: () => void
}

export const editMobileNumberModalContextDefaults: EditMobileNumberModalContextProps = {
  step: EditMobileNumberSteps.Init,
  setStep: noop,
  token: '',
  setToken: noop,
  newMobile: '',
  setNewMobile: noop,
  resetState: noop,
}

export const EditMobileNumberModalContext = createContext<EditMobileNumberModalContextProps>(editMobileNumberModalContextDefaults)

export const useEditMobileNumberModalContext = (): EditMobileNumberModalContextProps => {
  return { ...useContext(EditMobileNumberModalContext) }
}

export interface EditMobileNumberModalProviderProps {
  children: React.ReactNode
}

export const EditMobileNumberModalProvider: FunctionComponent<EditMobileNumberModalProviderProps> = ({ children }) => {
  const initialStep = EditMobileNumberSteps.Init
  const [step, setStep] = useState<EditMobileNumberStep>(initialStep)
  const [token, setToken] = useState<string>('')
  const [newMobile, setNewMobile] = useState<string>('')
  const resetState = useCallback((): void => {
    setStep(initialStep)
    setToken('')
  }, [initialStep])

  const value = {
    step,
    setStep,
    token,
    setToken,
    newMobile,
    setNewMobile,
    resetState,
  }

  return <EditMobileNumberModalContext.Provider value={value}>{children}</EditMobileNumberModalContext.Provider>
}

interface EditMobileNumberModalProps {
  isModalOpen: boolean
  closeModal: () => void
}

export const EditMobileNumberModalSteps: FunctionComponent<EditMobileNumberModalProps> = ({ isModalOpen, closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const setToast = useToast()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { step, setToken, resetState } = useContext(EditMobileNumberModalContext)
  const renderStep = useCallback((): React.ReactNode => {
    switch (step) {
      case EditMobileNumberSteps.Init:
        return <Init />
      case EditMobileNumberSteps.Edit:
        return <Edit />
      case EditMobileNumberSteps.Confirm:
        return <Confirm closeModal={closeModal} />
    }
  }, [closeModal, step])

  useEffect(() => {
    if (!isModalOpen) {
      resetState()
    }
  }, [isModalOpen, resetState])

  const [verifyEmail, { data: verifyEmailData, isError: isVerifyEmailError, isLoading: isVerifyEmailLoading }] = useChangeMobileVerifyEmailMutation()

  const requestEmailCode = useCallback(async (): Promise<void> => {
    try {
      const res = (await verifyEmail()) as { data: PostVerifyEmailResponseBody }
      setToken(res.data.token)
    } catch (err) {
      closeModal()
      setToast({ message: t('account:edit:mobile:toast:error'), kind: 'Failure', manualDismiss: true, toastTime: 2000, testNameSpace })
    }
  }, [setToken, verifyEmail])

  useEffect(() => {
    requestEmailCode()
  }, [requestEmailCode])

  const onShow = (): void => {
    logEvent(TrackingEvent.CLICKED_EDIT_MOBILE)
  }

  const testNameSpace = 'edit-mobile-number-modal'

  return (
    <Modal
      show={isModalOpen}
      onShow={onShow}
      onClose={closeModal}
      onHide={closeModal}
      onBackdropClick={closeModal}
      onEscapeKeyDown={closeModal}
      loading={isVerifyEmailLoading}
      testNameSpace='edit-mobile-number'
    >
      {isVerifyEmailLoading && <div className='py-32'></div>}
      {/* {isVerifyEmailError && <div>Error</div>} */}
      {verifyEmailData != null && renderStep()}
    </Modal>
  )
}

export const EditMobileNumberModal: FunctionComponent<EditMobileNumberModalProps> = ({ isModalOpen, closeModal }) => {
  return isModalOpen ? (
    <EditMobileNumberModalProvider>
      <EditMobileNumberModalSteps isModalOpen={isModalOpen} closeModal={closeModal} />
    </EditMobileNumberModalProvider>
  ) : null
}
